import { useState } from 'react';
import { File as FileIcon, Image, Loader2, Music, Paperclip, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AttachMediaDialog } from '@/components/inbox/AttachMediaDialog';
import type { AttachmentKind } from '@/lib/inbox/attachmentRules';

// Same menu as the inbox composer's attach button.
const MENU_ITEM =
  'cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-[14px] font-medium text-[#364153] focus:bg-[#F4F6FA] focus:text-[#101828]';

const OPTIONS: { kind: AttachmentKind; label: string; Icon: typeof Image }[] = [
  { kind: 'image', label: 'Send image', Icon: Image },
  { kind: 'video', label: 'Send video', Icon: Video },
  { kind: 'audio', label: 'Send audio', Icon: Music },
  { kind: 'document', label: 'Send document', Icon: FileIcon },
];

export interface TeamChatAttachButtonProps {
  /** Upload and send the picked files; resolves when done. */
  onSendFiles: (kind: AttachmentKind, files: File[]) => Promise<void>;
  disabled?: boolean;
  uploading?: boolean;
  className?: string;
}

/**
 * Team chat's attach button: pick a type, then the inbox's file dialog
 * (drag and drop, paste, type and size checks) for that type.
 */
export function TeamChatAttachButton({ onSendFiles, disabled, uploading, className }: TeamChatAttachButtonProps) {
  const [kind, setKind] = useState<AttachmentKind | null>(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={className}
            disabled={disabled || uploading}
            title="Attach file"
            data-testid="team-chat-attach"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[200px] rounded-xl border-[#EAECF0] p-1.5 shadow-[0_12px_32px_rgba(16,24,40,0.12)]"
        >
          {OPTIONS.map(({ kind: k, label, Icon }) => (
            <DropdownMenuItem key={k} className={MENU_ITEM} onClick={() => setKind(k)} data-testid={`team-chat-attach-${k}`}>
              <Icon className="h-4 w-4" />
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {kind && (
        <AttachMediaDialog
          open={!!kind}
          onOpenChange={(next) => { if (!next) setKind(null); }}
          channel="teamchat"
          kind={kind}
          alreadyAttached={0}
          onAdd={(files) => { void onSendFiles(kind, files); }}
        />
      )}
    </>
  );
}
